import * as React from 'react';
import { useServiceReducer, Action } from './util/fetchReducer';
import _defaultsDeep from 'lodash/defaultsDeep';
import { v4 as uuidv4 } from 'uuid';
import { AxiosError, AxiosResponse } from 'axios';
import useServiceAuth from './../useServiceAuth';
import { ConfigState } from '../../redux/modules/config/reducer';

interface Options {
  id?: string;
  name?: string | null;
  log?: {
    start?: boolean;
    success?: boolean;
    error?: boolean;
  };
  rethrowErrors?: boolean;
}

export type LogLevels = 'log' | 'error' | 'warn';

const consoleLog = (
  id: string,
  message: string | Record<string, any>,
  level: LogLevels = 'log',
) => {
  // eslint-disable-next-line no-console
  console.group(`Fetch ID: ${id}`);
  // eslint-disable-next-line no-console
  console[level](message);
  // eslint-disable-next-line no-console
  console.groupEnd();
};

const getActions = <T>() => ({
  start: (): Action<T> => ({ type: 'START' }),
  error: (error: any): Action<T> => ({ type: 'ERROR', error }),
  success: (response: AxiosResponse<T>): Action<T> => ({
    type: 'SUCCESS',
    response,
  }),
  reset: (): Action<T> => ({ type: 'RESET' }),
});

export type ServiceAuth = {
  authToken: string;
  account: string;
  config: ConfigState;
};

export type ResetSignature = () => void;

export type ServiceState<T> = {
  isFetching: boolean;
  error?: AxiosError;
  response: AxiosResponse<null | T>;
  lastFetchAttempt: null | Date;
};

export type Service<TReturnType, TArgs extends Array<any>> = (
  auth: ServiceAuth,
) => AuthedService<TReturnType, TArgs>;
export type AuthedService<TReturnType, TArgs extends Array<any>> = (
  ...args: TArgs
) => Promise<AxiosResponse<TReturnType>>;
type ReturnVal<T, TDataType> = [T, ServiceState<TDataType>, ResetSignature];

const useService = function <TReturnType, TArgs extends Array<any>>(
  service: Service<TReturnType, TArgs>,
  options?: Options,
): ReturnVal<AuthedService<TReturnType, TArgs>, TReturnType> {
  const serviceRef = React.useRef(service);
  const [fetchState, dispatch] = useServiceReducer();
  const accountAuthToken = useServiceAuth();

  const defaultOptions = React.useRef({
    id: uuidv4(),
    name: null,
    log: { start: false, success: false, error: false },
    rethrowErrors: false,
  });

  // Options are read when the executor runs, not when the callback is created.
  // Otherwise a caller passing an inline object (e.g. `{ rethrowErrors: true }`)
  // would get a new executor every render and break downstream memoization.
  const optionsRef = React.useRef(options);
  optionsRef.current = options;

  const executor = React.useCallback(
    async (...args: any[]) => {
      let response: AxiosResponse<TReturnType>;
      const { id, log, rethrowErrors } = _defaultsDeep(
        { ...optionsRef.current },
        defaultOptions.current,
      );
      const actions = getActions<TReturnType>();

      try {
        dispatch(getActions<AxiosResponse<TReturnType>>().start());
        log.start && consoleLog(id, actions.start());
        response = await serviceRef.current(accountAuthToken)(
          ...(args as TArgs),
        );

        dispatch(actions.success(response));
        log.success && consoleLog(id, actions.success(response));

        return response;
      } catch (error) {
        const axiosError: AxiosError = error;
        console.error(axiosError);
        dispatch(actions.error(axiosError));
        log.error && consoleLog(id, actions.error(axiosError));
        if (rethrowErrors) throw axiosError;

        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return response;
      }
    },
    [dispatch, accountAuthToken],
  );

  const reset = React.useCallback(() => {
    dispatch(getActions<TReturnType>().reset());
  }, [dispatch]);

  return [executor, fetchState, reset];
};

export default useService;
